import { useInfiniteQuery } from '@tanstack/react-query'
import { Empty, Flex, SelectProps, Spin, Select } from 'antd'
import { useState } from 'react'

import { useCounterpartServiceGetCounterpartListEndpointKey } from 'src/api/queries'
import {
  CounterpartService,
  GetCounterpartListResponseItem,
  GetFormListResponseItem
} from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import { useDebounce } from 'src/hooks'

type Props = {
  filterId?: string
} & SelectProps

export const CounterPartSelect = ({ filterId, ...props }: Props) => {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)

  const { data, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: [
      useCounterpartServiceGetCounterpartListEndpointKey,
      debouncedSearch
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await CounterpartService.getCounterpartListEndpoint(
        pageParam,
        DEFAULT_PER_PAGE,
        debouncedSearch
      )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data?: GetCounterpartListResponseItem[]
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const options = data?.pages.flatMap(
    (page) =>
      page.data
        ?.map((item: GetFormListResponseItem) => ({
          value: item?.id,
          label: item?.name
        }))
        .filter((item) => item?.value !== filterId) ?? []
  )

  return (
    <Select
      allowClear
      popupMatchSelectWidth={true}
      filterOption={false}
      options={options}
      onSearch={(value) => setSearch(value)}
      onClear={() => setSearch('')}
      loading={isFetching}
      showSearch={true}
      onPopupScroll={(e) => {
        const target = e.target as HTMLElement
        if (target.scrollTop + target.offsetHeight === target.scrollHeight)
          fetchNextPage()
      }}
      notFoundContent={
        isFetching ? (
          <Flex justify="center">
            <Spin spinning />
          </Flex>
        ) : (
          <Empty />
        )
      }
      {...props}
    />
  )
}
